import { useMemo, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack5';

// Custom React component for the snackbar message with live countdown
function RateLimitSnackbar({ initialSeconds }) {
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    if (seconds <= 0) return;
    const interval = setInterval(() => {
      setSeconds((s) => s - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [seconds]);

  return (
    <span>
      Too many requests. Please try again after <b>{seconds}</b> second{seconds !== 1 ? 's' : ''}.
    </span>
  );
}

export const useAxiosWithRetry = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Use refs to persist timer and snackbarKey across renders
  const timerIdRef = useRef(null);
  const snackbarKeyRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
      if (snackbarKeyRef.current) closeSnackbar(snackbarKeyRef.current);
    };
    // eslint-disable-next-line
  }, []);

  const axiosInstance = useMemo(() => {
    const instance = axios.create();

    // Response interceptor to handle 429 errors
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'] || 10;
          let retryTime = parseInt(retryAfter, 10);
          // Ensure minimum 10 seconds
          if (Number.isNaN(retryTime) || retryTime < 10) retryTime = 10;

          // Show error message with countdown
          snackbarKeyRef.current = enqueueSnackbar(
            <RateLimitSnackbar initialSeconds={retryTime} />,
            {
              variant: 'error',
              autoHideDuration: retryTime * 1000,
              preventDuplicate: true,
              key: 'rate-limit-error'
            }
          );

          // Wait for the retry time
          await new Promise(resolve => {
            timerIdRef.current = setTimeout(resolve, retryTime * 1000);
          });

          // Close the snackbar if still open
          if (snackbarKeyRef.current) {
            closeSnackbar(snackbarKeyRef.current);
          }

          // Retry the original request
          return instance.request(error.config);
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [enqueueSnackbar, closeSnackbar]);

  return axiosInstance;
};
